import { Zap, CreditCard, RotateCw, Bell, Shield } from 'lucide-react';
import { Language, translations } from '../i18n/translations';

export type TabDestination = 'usage' | 'billing' | 'scraper' | 'alerts' | 'settings';

interface BottomTabBarProps {
  activeTab: TabDestination;
  onChangeTab: (tab: TabDestination) => void;
  unreadAlertsCount: number;
  lang?: Language;
}

export function BottomTabBar({
  activeTab,
  onChangeTab,
  unreadAlertsCount,
  lang = 'ja'
}: BottomTabBarProps) {
  const t = translations[lang];

  const tabs = [
    { id: 'usage' as TabDestination, label: t.tabUsage, icon: Zap },
    { id: 'billing' as TabDestination, label: t.tabBilling, icon: CreditCard },
    { id: 'scraper' as TabDestination, label: t.tabSync, icon: RotateCw },
    { id: 'alerts' as TabDestination, label: t.tabAlerts, icon: Bell, badge: unreadAlertsCount },
    { id: 'settings' as TabDestination, label: t.tabSettings, icon: Shield }
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-slate-950/90 backdrop-blur-xl border-t border-slate-800/80 pb-safe">
      <div className="max-w-md mx-auto grid grid-cols-5 items-center h-16 px-1">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChangeTab(tab.id)}
              className={`flex flex-col items-center justify-center min-h-[48px] py-1 transition-all relative ${
                isActive
                  ? 'text-teal-400 font-bold scale-[1.03]'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <div className="relative">
                <Icon className={`w-5 h-5 transition-transform ${isActive ? 'stroke-[2.5px]' : 'stroke-[1.8px]'}`} />
                {tab.badge && tab.badge > 0 ? (
                  <span className="absolute -top-1 -right-2 min-w-[15px] h-[15px] bg-teal-500 text-slate-950 text-[9px] font-black rounded-full flex items-center justify-center px-0.5">
                    {tab.badge}
                  </span>
                ) : null}
              </div>

              <span className={`text-[10px] mt-1 tracking-tight ${isActive ? 'font-bold text-teal-300' : 'font-medium'}`}>
                {tab.label}
              </span>

              {/* Active accent underline indicator */}
              {isActive && (
                <span className="absolute bottom-0 w-5 h-0.5 bg-teal-400 rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
